import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./Dropdowns.module.css";
import { SmallModal } from "../../components/UI/SmallModal/SmallModal";
import { EquipmentModal } from "./EquipmentModal";
import { useTextTruncate } from "../../hooks/useTextTruncate";
import { PaidEquipmentModal } from "./PaidEquipmentModal";
import { resolvePaidEquipmentImageSrc, formatPricePerPiece, normalizePaidEquipmentInput } from "./paidEquipmentHelpers";
import { FAKE_PAID_EQUIPMENT } from "../../data/fakePaidEquipment";
import { useModalSearchParamHistory } from "../../hooks/useModalSearchParamHistory";

export { FAKE_PAID_EQUIPMENT };
export { normalizePaidEquipmentInput } from "./paidEquipmentHelpers";

/** When true, studio profile still mounts equipment dropdowns so demo paid list can show if API is empty. Set false in production when no placeholder data is desired. */
export const SHOW_FAKE_PAID_EQUIPMENT_SECTION = true;

const ChevronLeftIcon = ({ muted }) => {
    const fill = muted ? "#CED1D5" : "#1F1F1F";
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden>
            <path fillRule="evenodd" clipRule="evenodd" d="M0 6.72018L6.74891 0L7.95099 1.19696L1.20208 7.91714L0 6.72018Z" fill={fill} />
            <path fillRule="evenodd" clipRule="evenodd" d="M6.74978 14L0 6.72018L1.25003 5.57164L7.99894 12.8518L6.74978 14Z" fill={fill} />
        </svg>
    );
};

const ChevronRightIcon = ({ muted }) => {
    const fill = muted ? "#CED1D5" : "#1F1F1F";
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden>
            <path fillRule="evenodd" clipRule="evenodd" d="M7.99894 7.27982L1.25003 14L0.0479515 12.803L6.79686 6.08286L7.99894 7.27982Z" fill={fill} />
            <path fillRule="evenodd" clipRule="evenodd" d="M1.24917 0L7.99894 7.27982L6.74891 8.42836L0 1.14817L1.24917 0Z" fill={fill} />
        </svg>
    );
};

function PaidEquipmentCardTitle({ title, className }) {
    const { textRef, containerRef, truncatedText } = useTextTruncate(title || "", { charsToReplace: 3 });
    return (
        <h4 ref={containerRef} className={className}>
            <span ref={textRef} className={styles.paidEquipmentTitleMeasure}>
                {truncatedText}
            </span>
        </h4>
    );
}

export const PaidEquipmentCarousel = ({ items, studioId }) => {
    const viewportRef = useRef(null);
    const [searchParams] = useSearchParams();

    const {
        isOpen: paidModalOpen,
        openOverlay,
        closeOverlay: closePaidModal,
        replaceParam,
    } = useModalSearchParamHistory({
        paramName: "paidEquipment",
        isActiveInUrl: (url) => url.searchParams.has("paidEquipment"),
        resetKey: studioId,
    });

    const paidParam = searchParams.get("paidEquipment");
    const paidModalView = !paidParam || paidParam === "open" ? "list" : "detail";
    const paidModalItem =
        paidModalView === "detail"
            ? items.find((item) => item?.id != null && String(item.id) === paidParam)
            : null;

    const openPaidModalList = () => {
        openOverlay("open");
    };

    const openPaidModalDetail = (item) => {
        const id = item?.id != null ? String(item.id) : null;
        if (!id) return;
        if (paidModalOpen) {
            replaceParam(id);
        } else {
            openOverlay(id);
        }
    };

    const backToPaidList = () => {
        replaceParam("open");
    };

    const [scrollState, setScrollState] = useState({ atStart: true, atEnd: true });
    const [desktopCarousel, setDesktopCarousel] = useState(() =>
        typeof window !== "undefined" ? window.innerWidth >= 742 : true
    );
    /** Matches @container paid-equipment (max-width: 529px) → 2 cards; otherwise 3 on desktop */
    const [visibleSlots, setVisibleSlots] = useState(3);

    useEffect(() => {
        const onResize = () => setDesktopCarousel(window.innerWidth >= 742);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const updateVisibleSlots = useCallback(() => {
        const el = viewportRef.current;
        if (!el || !desktopCarousel) {
            setVisibleSlots(3);
            return;
        }
        setVisibleSlots(el.clientWidth <= 529 ? 2 : 3);
    }, [desktopCarousel]);

    const updateScrollEdges = useCallback(() => {
        const el = viewportRef.current;
        if (!el) return;
        updateVisibleSlots();
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setScrollState({
            atStart: scrollLeft <= 2,
            atEnd: scrollLeft + clientWidth >= scrollWidth - 2,
        });
    }, [updateVisibleSlots]);

    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return;
        updateScrollEdges();
        el.addEventListener("scroll", updateScrollEdges);
        return () => el.removeEventListener("scroll", updateScrollEdges);
    }, [items.length, desktopCarousel, updateScrollEdges]);

    useLayoutEffect(() => {
        updateScrollEdges();
    }, [items.length, desktopCarousel, updateScrollEdges]);

    useEffect(() => {
        window.addEventListener("resize", updateScrollEdges);
        return () => window.removeEventListener("resize", updateScrollEdges);
    }, [updateScrollEdges]);

    const scrollByPage = (direction) => {
        const el = viewportRef.current;
        if (!el) return;
        el.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
    };

    const showAllClick = () => {
        openPaidModalList();
    };

    const count = items.length;
    const showNav = desktopCarousel && count > visibleSlots;

    return (
        <div className={styles.paidEquipment}>
            <div
                className={
                    showNav
                        ? styles.paidEquipmentToolbar
                        : `${styles.paidEquipmentToolbar} ${styles.paidEquipmentToolbarNoNav}`
                }
            >
                <button type="button" className={`${styles.showAllButton} regularMiddle`} onClick={showAllClick}>
                    Alle anschauen ({count})
                </button>
                {showNav && (
                    <div className={styles.paidEquipmentNav}>
                        <button
                            type="button"
                            className={styles.paidEquipmentNavBtn}
                            disabled={scrollState.atStart}
                            onClick={() => scrollByPage(-1)}
                            aria-label="Zurück"
                        >
                            <ChevronLeftIcon muted={scrollState.atStart} />
                        </button>
                        <button
                            type="button"
                            className={styles.paidEquipmentNavBtn}
                            disabled={scrollState.atEnd}
                            onClick={() => scrollByPage(1)}
                            aria-label="Weiter"
                        >
                            <ChevronRightIcon muted={scrollState.atEnd} />
                        </button>
                    </div>
                )}
            </div>
            <div ref={viewportRef} className={styles.paidEquipmentViewport}>
                <div className={styles.paidEquipmentTrack}>
                    {items.map((item, index) => {
                        const key = item.id != null ? String(item.id) : `paid-equipment-${index}`;
                        const title = item.nameModel || item.name || item.title || "Equipment";
                        const imageSrc = resolvePaidEquipmentImageSrc(item);
                        return (
                            <article
                                key={key}
                                className={styles.paidEquipmentCard}
                                role="button"
                                tabIndex={0}
                                onClick={() => openPaidModalDetail(item)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        openPaidModalDetail(item);
                                    }
                                }}
                            >
                                <div className={styles.paidEquipmentImageBox}>
                                    {imageSrc ? (
                                        <img src={imageSrc} alt={title} />
                                    ) : (
                                        <span className="regularMiddle" style={{ color: "#696F76", textAlign: "center" }}>
                                            {item.category || ""}
                                        </span>
                                    )}
                                </div>
                                <PaidEquipmentCardTitle
                                    title={title}
                                    className={`${styles.paidEquipmentTitle} regularLarge`}
                                />
                                <p className={`${styles.paidEquipmentPrice} regularSmall`}>
                                    {formatPricePerPiece(item.pricePerPiece ?? item.price)}
                                </p>
                            </article>
                        );
                    })}
                </div>
            </div>
            <SmallModal
                style={{ outline: "none" }}
                open={paidModalOpen}
                onBackdropClick={closePaidModal}
                paidEquipmentModal
            >
                <PaidEquipmentModal
                    items={items}
                    view={paidModalView}
                    selectedItem={paidModalItem}
                    onClose={closePaidModal}
                    onBackToList={backToPaidList}
                    onSelectItem={openPaidModalDetail}
                />
            </SmallModal>
        </div>
    );
};

const Dropdown = ({ title, children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [height, setHeight] = useState(0);
    const dropdownContentRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setHeight(dropdownContentRef.current.scrollHeight);
        } else {
            setHeight(0);
        }
    }, [isOpen]);

    return (
        <div className={styles.dropdown}>
            <div
                className={styles.dropdownHeader}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="titleSemibold3">{title}</span>
                {isOpen ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="9" viewBox="0 0 18 9" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M6.37684e-07 7.29426L9.09841 7.34491e-07L17.5 7.29426L16.3951 8.47011L9.04864 2.09261L1.05518 8.5L6.37684e-07 7.29426Z" fill="#1F1F1F"/>
                    </svg>
                ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="10" viewBox="0 0 18 10" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M17.5 2.1491L8.40159 9.44336L0 2.1491L1.10495 0.973254L8.45136 7.35075L16.4448 0.943359L17.5 2.1491Z" fill="#1F1F1F"/>
                    </svg>
                )}
            </div>
            <div 
                ref={dropdownContentRef}
                className={`${styles.dropdownContent} ${isOpen ? styles.open : ""}`}
                style={{ height: `${height}px`, overflow: 'hidden', transition: 'height 0.3s ease' }}
            >
                {children}
            </div>
            <div className={styles.line}></div>
        </div>
    );
};

export const EquipmentDropdown = ({ equipment, studioId }) => {
    const [searchParams] = useSearchParams();
    const [selectedEquipment, setSelectedEquipment] = useState(null);

    const {
        isOpen,
        openOverlay,
        closeOverlay,
    } = useModalSearchParamHistory({
        paramName: "equipment",
        isActiveInUrl: (url) => url.searchParams.has("equipment"),
        resetKey: studioId,
    });

    const [isMobile, setIsMobile] = useState(() => {
        // Initialize with current window width
        if (typeof window !== 'undefined') {
            return window.innerWidth < 480;
        }
        return false;
    });

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 480);
        };
        
        // Call immediately to set initial state
        handleResize();
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setSelectedEquipment(null);
            return;
        }
        const category = searchParams.get("equipment");
        if (!category) return;
        const match = equipment?.find((item) => item?.category === category);
        if (match) setSelectedEquipment(match);
    }, [isOpen, searchParams, equipment]);

    const handleEquipmentClick = (item) => {
        if (!item?.category) return;
        setSelectedEquipment(item);
        openOverlay(item.category);
    };

    const handleModalClose = () => {
        closeOverlay();
    };

    return (
        <div className={styles.equipmentDropdown}>
            {equipment.map((item) => {
                const hasItems = item?.items && item?.items?.length > 0;
                return (
                    <div
                        onClick={hasItems ? () => handleEquipmentClick(item) : undefined} 
                        className={styles.equipmentDropdownItemContainer} 
                        key={item?.id}
                    >
                        <div className={`${styles.equipmentDropdownItem} ${!hasItems ? styles.disabled : ''}`}>
                            <span className="regularMiddle">{item?.category}</span>
                            {hasItems && (
                                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M1.99747 0.505859L7.32559 6.27798L7.32558 6.27799L7.32559 6.278L1.99747 11.6061L1.00752 10.6162L5.38451 6.23918L0.96875 1.45545L1.99747 0.505859Z" fill="#1F1F1F"/>
                                </svg>
                            )}
                        </div>
                        <div className={styles.smallLine}></div>
                    </div>
                );
            })}
            <div className={styles.equipmentModal}>
            <EquipmentModal
                selectedEquipment={selectedEquipment}
                isOpen={isOpen}
                onClose={handleModalClose}
            />
            </div>
            {/* Only render SmallModal when NOT mobile */}
            {!isMobile && (
                <SmallModal 
                    style={{ outline: 'none' }} 
                    open={isOpen} 
                    onBackdropClick={handleModalClose}
                >
                    <EquipmentModal 
                        style={{ outline: 'none' }} 
                        selectedEquipment={selectedEquipment} 
                        isOpen={isOpen} 
                        onClose={handleModalClose}
                    />
                </SmallModal>
            )}
        </div>
    );
}

const HOUSE_RULE_COPY = {
    smokingAllowed: {
        allowed: "Rauchen & Dampfen erlaubt",
        blocked: "Rauchen & Dampfen nicht erlaubt",
    },
    alcoholAllowed: {
        allowed: "Alkoholkonsum erlaubt",
        blocked: "Alkoholkonsum nicht erlaubt",
    },
    foodAllowed: {
        allowed: "Essen im Studiobereich erlaubt",
        blocked: "Essen im Studiobereich nicht erlaubt",
    },
    adultContentAllowed: {
        allowed: "Erwachsenen-Inhalte oder Nacktdrehs erlaubt",
        blocked: "Erwachsenen-Inhalte oder Nacktdrehs nicht erlaubt",
    },
    fogMachineAllowed: {
        allowed: "Nebelmaschinen erlaubt",
        blocked: "Nebelmaschinen nicht erlaubt",
    },
    glitterConfettiAllowed: {
        allowed: "Glitzer, Konfetti oder kleine Partikel erlaubt",
        blocked: "Glitzer, Konfetti oder kleine Partikel nicht erlaubt",
    },
    pyrotechnicsAllowed: {
        allowed: "Flammen & Pyrotechnik erlaubt",
        blocked: "Flammen & Pyrotechnik nicht erlaubt",
    },
    equipmentChangesAllowed: {
        allowed: "Änderungen an der Studio-Ausstattung erlaubt",
        blocked: "Änderungen an der Studio-Ausstattung nicht erlaubt",
    },
};

const CANCELLATION_POLICY_COPY = {
    FLEXIBLE: {
        title: "Kulant",
        lines: [
            { prefix: "Bis 48 Stunden vor Beginn: ", highlight: "Volle Rückerstattung" },
            { prefix: "Bis 24 Stunden vor Beginn: ", highlight: "50% Rückerstattung" },
            { prefix: "Unter 24 Stunden vor Beginn: ", highlight: "Keine Rückerstattung" },
        ],
    },
    STANDARD: {
        title: "Standard",
        lines: [
            { prefix: "Bis 5 Tage vor Beginn: ", highlight: "Volle Rückerstattung" },
            { prefix: "Bis 48 Stunden vor Beginn: ", highlight: "50% Rückerstattung" },
            { prefix: "Unter 48 Stunden vor Beginn: ", highlight: "Keine Rückerstattung" },
        ],
    },
    STRICT: {
        title: "Strikt",
        lines: [
            { prefix: "Bis 14 Tage vor Beginn: ", highlight: "Volle Rückerstattung" },
            { prefix: "Bis 7 Tage vor Beginn: ", highlight: "50% Rückerstattung" },
            { prefix: "Unter 7 Tage vor Beginn: ", highlight: "Keine Rückerstattung" },
        ],
    },
};

const formatHouseRules = (houseRules) => {
    if (!houseRules) return [];

    return Object.entries(HOUSE_RULE_COPY)
        .filter(([key]) => typeof houseRules[key] === "boolean")
        .map(([key, text]) => (houseRules[key] ? text.allowed : text.blocked));
};

const getCancellationPolicyConfig = (rawValue) => {
    if (!rawValue) return null;

    const trimmed = rawValue.trim();
    const upper = trimmed.toUpperCase();

    if (CANCELLATION_POLICY_COPY[upper]) {
        return CANCELLATION_POLICY_COPY[upper];
    }

    // Fallback for values stored in creation-form format (kulant/standard/strikt)
    const lower = trimmed.toLowerCase();
    if (lower === "kulant") return CANCELLATION_POLICY_COPY.FLEXIBLE;
    if (lower === "standard") return CANCELLATION_POLICY_COPY.STANDARD;
    if (lower === "strikt") return CANCELLATION_POLICY_COPY.STRICT;

    return null;
};

const Dropdowns = ({ studioId, equipment, paidEquipment = [], houseRules, cancellationPolicy }) => {
    const freeItems = equipment ?? [];
    const paidFromApi = normalizePaidEquipmentInput(paidEquipment);
    const paidItems = paidFromApi.length > 0 ? paidFromApi : FAKE_PAID_EQUIPMENT;
    const houseRulesList = formatHouseRules(houseRules);
    const customRule = houseRules?.customRules?.trim();
    const formattedRule = customRule
        ? customRule.charAt(0).toUpperCase() + customRule.slice(1)
        : customRule;
    const hasCancellationPolicy = typeof cancellationPolicy === "string" && cancellationPolicy.trim().length > 0;
    const cancellationPolicyConfig = hasCancellationPolicy
        ? getCancellationPolicyConfig(cancellationPolicy)
        : null;

    return (
        <div className={styles.container}>
            {freeItems.length > 0 && (
                <Dropdown title="Kostenloses Equipment">
                    <EquipmentDropdown equipment={freeItems} studioId={studioId} />
                </Dropdown>
            )}
            {paidItems.length > 0 && (
                <Dropdown title="Kostenpflichtiges Equipment">
                    <PaidEquipmentCarousel items={paidItems} studioId={studioId} />
                </Dropdown>
            )}
            {/* <Dropdown title="Im Studio enthalten">
                <p>Inhalt für 'Im Studio enthalten'.</p>
            </Dropdown> */}
            {/* <Dropdown title="Optional buchbares Equipment">
                <p>Inhalt für 'Optional buchbares Equipment'.</p>
            </Dropdown> */}
            {/* <Dropdown title="Betriebszeiten">
                <p>Inhalt für 'Betriebszeiten'.</p>
            </Dropdown> */}
            {(houseRulesList.length > 0 || formattedRule) && (
                <Dropdown title="Hausregeln">
                    <div className={styles.houseRulesListContainer}>
                        <h1 className="semiboldMiddle">Hausregeln</h1>
                        <ul className={styles.houseRulesList}>
                        {houseRulesList.map((rule) => (
                            <li key={rule} className="regularSmall">{rule}</li>
                        ))}
                        {formattedRule && 
                            <li className={styles.customRuleItem}>
                                <span className="regularSmall">Zusätzliche Regeln:</span>
                                <span className={`${styles.customRuleText} regularSmall`}>{formattedRule}</span>
                            </li>
                        }
                        </ul>
                    </div>
                </Dropdown>
            )}
            {hasCancellationPolicy && (
                <Dropdown title="Stornierungsbedingungen">
                    {cancellationPolicyConfig ? (
                        <div className={styles.cancellationPolicyBox}>
                            <h2 className="titleSemibold3">{cancellationPolicyConfig.title}</h2>
                            <div className={styles.cancellationPolicyLines}>
                                {cancellationPolicyConfig.lines.map((line) => (
                                    <span key={line.prefix} className="regularSmall">
                                        {line.prefix}
                                        <br />
                                        <span className={styles.cancellationPolicyHighlight}>
                                            {line.highlight}
                                        </span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p className="regularMiddle">{cancellationPolicy}</p>
                    )}
                </Dropdown>
            )}
        </div>
    );
};

export default Dropdowns;
